import { Box, Theme } from '@mui/material';
import { useMemo } from 'react';

import { SizingMultiplier, Typography } from '@ocx/ui';
import { CalendarIcon, TimerIcon } from '@ocx/ui-icons';

const ICON_STYLE = { width: 16, height: 16, flexShrink: 0, opacity: 0.6 };

export type ExternalBannerChipData = {
  icon?: 'timer' | 'calendar';
  style?: 'error';
  label: string;
};

const getIcon = (icon?: ExternalBannerChipData['icon']) => {
  if (icon === 'timer') {
    return <TimerIcon sx={ICON_STYLE} />;
  }
  if (icon === 'calendar') {
    return <CalendarIcon sx={ICON_STYLE} />;
  }
  return null;
};

const getStyle = (theme: Theme, style?: ExternalBannerChipData['style']) => {
  if (style === 'error') {
    return {
      color: theme.palette.error.main,
      backgroundColor: theme.palette.error.light,
    };
  }
  return {};
};

export type ExternalBannerChipProps = ExternalBannerChipData;

export const ExternalBannerChip = ({ icon, style, label }: ExternalBannerChipProps) => {
  const iconEl = useMemo(() => getIcon(icon), [icon]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: SizingMultiplier.xs,
        px: SizingMultiplier.sm,
        py: SizingMultiplier.xs,
        borderRadius: '8px',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.selected,
        ...getStyle(theme, style),
      })}>
      {iconEl}
      <Typography
        variant="body2"
        color="inherit"
        fontSize="small"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          direction: 'rtl',
          textAlign: 'left',
        }}>
        {label}
      </Typography>
    </Box>
  );
};
